import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Calendar — grille mensuelle réutilisable (single date ou range).
 *
 *   <Calendar
 *     mode="single"
 *     selected={date}
 *     onSelect={setDate}
 *     month={visibleMonth}
 *     onMonthChange={setVisibleMonth}
 *     minDate={new Date()}
 *   />
 *
 *   <Calendar
 *     mode="range"
 *     selected={{ from, to }}
 *     onSelect={({from, to}) => ...}
 *   />
 *
 * Pas de lib externe — Date natif, locale fr-FR, semaine Lundi→Dimanche.
 */

const WEEKDAYS_SHORT = ['Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa', 'Di'];

const MONTH_FMT = new Intl.DateTimeFormat('fr-FR', { month: 'long', year: 'numeric' });

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function startOfDay(d)   { return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }

/**
 * Décale au lundi qui ouvre la grille du mois affiché.
 */
function startOfCalendarGrid(monthStart) {
  const d = new Date(monthStart);
  // getDay : 0=dim, 1=lun, …, 6=sam → on veut lundi en début
  const day = d.getDay();
  const diff = (day === 0 ? -6 : 1 - day);
  d.setDate(d.getDate() + diff);
  return d;
}

function isSameDay(a, b) {
  if (!a || !b) return false;
  return a.getFullYear() === b.getFullYear()
    && a.getMonth() === b.getMonth()
    && a.getDate() === b.getDate();
}

function isBefore(a, b) { return startOfDay(a).getTime() < startOfDay(b).getTime(); }
function isAfter(a, b)  { return startOfDay(a).getTime() > startOfDay(b).getTime(); }

function buildGrid(month) {
  const firstOfMonth = startOfMonth(month);
  const gridStart = startOfCalendarGrid(firstOfMonth);
  const days = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(gridStart);
    d.setDate(gridStart.getDate() + i);
    days.push(d);
  }
  return days;
}

export function Calendar({
  mode = 'single',
  selected,
  onSelect,
  month,
  onMonthChange,
  minDate,
  maxDate,
  className,
}) {
  const today = useMemo(() => startOfDay(new Date()), []);
  const visibleMonth = month || (mode === 'range' && selected?.from) || selected || today;

  const days = useMemo(() => buildGrid(visibleMonth), [visibleMonth]);
  const visMonthIdx = visibleMonth.getMonth();

  const prevMonth = () => {
    const d = new Date(visibleMonth);
    d.setMonth(d.getMonth() - 1);
    onMonthChange?.(d);
  };
  const nextMonth = () => {
    const d = new Date(visibleMonth);
    d.setMonth(d.getMonth() + 1);
    onMonthChange?.(d);
  };

  const isDisabled = (d) => {
    if (minDate && isBefore(d, minDate)) return true;
    if (maxDate && isAfter(d, maxDate)) return true;
    return false;
  };

  const isSelected = (d) => {
    if (mode === 'range') {
      const { from, to } = selected || {};
      if (from && isSameDay(d, from)) return true;
      if (to && isSameDay(d, to)) return true;
      return false;
    }
    return isSameDay(d, selected);
  };

  const isInRange = (d) => {
    if (mode !== 'range') return false;
    const { from, to } = selected || {};
    if (!from || !to) return false;
    return d > startOfDay(from) && d < startOfDay(to);
  };

  const handleSelect = (d) => {
    if (isDisabled(d)) return;
    if (mode === 'range') {
      const { from, to } = selected || {};
      // 1er clic OU complet → on repart d'un from
      if (!from || (from && to)) {
        onSelect?.({ from: startOfDay(d), to: null });
        return;
      }
      // 2e clic : on complète
      if (isBefore(d, from)) {
        onSelect?.({ from: startOfDay(d), to: from });
      } else {
        onSelect?.({ from, to: startOfDay(d) });
      }
      return;
    }
    onSelect?.(startOfDay(d));
  };

  const title = MONTH_FMT.format(visibleMonth)
    .replace(/^./, (s) => s.toUpperCase());

  return (
    <div className={cn('select-none', className)}>
      {/* Header : mois + nav */}
      <div className="flex items-center justify-between mb-2 px-1">
        <button
          type="button"
          onClick={prevMonth}
          className="w-7 h-7 rounded-md hover:bg-bg-2 inline-flex items-center justify-center text-mute hover:text-text transition-colors"
          aria-label="Mois précédent"
        >
          <ChevronLeft className="w-4 h-4" strokeWidth={1.8} />
        </button>
        <span className="text-sm font-semibold text-text tracking-tight">
          {title}
        </span>
        <button
          type="button"
          onClick={nextMonth}
          className="w-7 h-7 rounded-md hover:bg-bg-2 inline-flex items-center justify-center text-mute hover:text-text transition-colors"
          aria-label="Mois suivant"
        >
          <ChevronRight className="w-4 h-4" strokeWidth={1.8} />
        </button>
      </div>

      {/* Weekdays */}
      <div className="grid grid-cols-7 mb-1">
        {WEEKDAYS_SHORT.map((w) => (
          <div
            key={w}
            className="text-[10.5px] font-mono uppercase tracking-wider text-mute-2 text-center py-1"
          >
            {w}
          </div>
        ))}
      </div>

      {/* Grille */}
      <div className="grid grid-cols-7 gap-y-0.5">
        {days.map((d, i) => {
          const inMonth = d.getMonth() === visMonthIdx;
          const isToday = isSameDay(d, today);
          const sel = isSelected(d);
          const inRange = isInRange(d);
          const disabled = isDisabled(d);
          // Pour le range : arrondi à gauche/droite uniquement aux extrémités
          const isRangeFrom = mode === 'range' && selected?.from && isSameDay(d, selected.from);
          const isRangeTo = mode === 'range' && selected?.to && isSameDay(d, selected.to);

          return (
            <button
              key={i}
              type="button"
              onClick={() => handleSelect(d)}
              disabled={disabled}
              aria-pressed={sel}
              className={cn(
                'h-8 w-full text-[12.5px] tabular-nums inline-flex items-center justify-center transition-colors relative',
                disabled && 'opacity-30 pointer-events-none',
                !inMonth && !sel && 'text-mute-2',
                inMonth && !sel && !inRange && 'text-text hover:bg-bg-2 rounded-md',
                inRange && 'bg-cream-2 text-text',
                sel && 'bg-ink text-cream font-semibold',
                sel && mode === 'single' && 'rounded-md',
                sel && isRangeFrom && !selected?.to && 'rounded-md',
                sel && isRangeFrom && selected?.to && 'rounded-l-md',
                sel && isRangeTo && 'rounded-r-md',
                isToday && !sel && 'font-bold',
                isToday && !sel && 'after:content-["•"] after:absolute after:bottom-0 after:text-ink after:text-[10px] after:leading-none',
              )}
            >
              {d.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default Calendar;
